// Shared value-slot candidate policy — types (T7, Docs/AutocompleteValueSlot-Plan.md).
// Engine resolvers keep DISCOVERY (they know their snapshots, quoting, and
// extra scalar sources); this module owns legality, coercion bucketing,
// tiers, qualifier policy, keywords, and dedup (D10).

import type {
  AliasBinding,
  CompletionCandidate,
  DatabaseEngineId,
} from '@/core/engines';
import type { OperandLiteralSqlType } from '@/features/sql-resolver/types/queryStructure';

/** One in-scope column the engine resolver discovered for the slot. */
/** C2 remediation (P1-1/P1-2/P2-3) — a column of a VIRTUAL relation the
 *  schema snapshot never carries (system-catalog views, mssql synthetic
 *  relations, sqlite JSON TVFs). Engine resolvers pass a
 *  `virtualColumnsFor(binding)` closure into their adapter context so the
 *  adapter can fall back to it when the snapshot lookup yields nothing —
 *  data flows resolver→adapter as an argument, never as an import back. */
export type ValueSlotVirtualColumn = {
  readonly name: string;
  readonly dataType?: string;
};

export type ValueSlotColumnDescriptor = {
  /** Bare column name as displayed (`customer_id`). */
  readonly label: string;
  /** Insert text for the unambiguous bare form. */
  readonly insertBare: string;
  /** Insert text qualified through the owning binding — uses the alias's
   *  VERBATIM source spelling (`owner.aliasSql`, D8/P2-9). */
  readonly insertQualified: string;
  readonly owner: AliasBinding;
  /** Coarse declared type; 'unknown' for CTE/derived projections (D5). */
  readonly coarseType: OperandLiteralSqlType;
  /** Declared BOOL/BOOLEAN, MSSQL BIT, MySQL TINYINT(1) — D13 predicate. */
  readonly booleanish: boolean;
  /** True when this column is the FK partner of the slot's LHS column —
   *  `orderHint 0` within its tier (D7). */
  readonly fkPartnerOfLhs: boolean;
};

export type ValueSlotRoutineDescriptor = {
  readonly label: string;
  readonly insertText: string;
  readonly returnsCoarse: OperandLiteralSqlType;
  /** Raw introspected return spelling (`double precision`, `void`,
   *  `SETOF record`) — drives the dead-return drop (spec §A2) and the
   *  detail text. Absent when the engine snapshot carries no return
   *  metadata. */
  readonly returnsRaw?: string;
  /** Predicate-head plan PH-D4 (P1-sol F6): does this routine return a
   *  boolean-ish type? `returnsCoarse` CANNOT answer this off-pgsql —
   *  `coarseTypeOfDataType` maps mssql `bit` to 'unknown' and mysql
   *  `tinyint(1)` to 'integer', so boolean-return promotion at a predicate
   *  head would be silently inert on those engines. Adapter builders compute
   *  it per engine from `returnsRaw` via `isBooleanishDataType`. Unread by
   *  the value-slot policy — a predicate-head-only signal carried on the
   *  shared descriptor because PH-D3 reuses the adapter unchanged. */
  readonly returnsBooleanish?: boolean;
};

/** The engine adapter handed to `emitValueSlotCandidates` (D10). */
export type ValueSlotEngineAdapter = {
  readonly engine: DatabaseEngineId;
  /** mssql's column path is async (G27); sync engines return the array. */
  readonly collectScopedColumns: () =>
    | Promise<readonly ValueSlotColumnDescriptor[]>
    | readonly ValueSlotColumnDescriptor[];
  readonly collectScalarRoutines: () => readonly ValueSlotRoutineDescriptor[];
  /** Engine-owned extra scalar sources (oracle sequences, mssql/mysql
   *  variables…). The policy clamps their tier to ≥3. */
  readonly extraScalarSources?: () => readonly CompletionCandidate[];
  /** C2 head-parity (predicate-head plan) — every USER-TYPABLE alias binding
   *  in scope, so the predicate-head policy can emit a qualifier even for a
   *  binding whose columns could not be resolved (relation outside the
   *  snapshot/synonyms, failed subquery inference). Without it qualifiers are
   *  derived from resolved column descriptors' owners only, and such a
   *  binding contributes NOTHING — where oracle's clause path offered every
   *  alias name at a bare `ON` via `emitAliasNames` regardless of column
   *  resolution. Engines opt in (oracle today); internal synthetic aliases
   *  the user cannot type (e.g. `__pivot`) must be filtered out by the
   *  adapter. Unread by the value-slot policy. */
  readonly qualifierBindings?: () => readonly AliasBinding[];
};

/** Resolved LHS context the resolver computed via `resolveLhsInScope` (D5). */
export type ValueSlotScope = {
  /** Coarse LHS type; 'unknown' when unresolved or when multiple owners
   *  disagree (D5) — no tier demotion is possible then. */
  readonly lhsType: OperandLiteralSqlType;
  /** Whether the LHS column itself is boolean-ish (drives TRUE/FALSE). */
  readonly lhsBooleanish: boolean;
};

/** Map a declared SQL data type to the coarse operand class used by the
 *  compat table. Anything unrecognized (dates, uuids, binary, spatial…)
 *  is 'unknown' → neutral, never demoted (D6). MySQL/MariaDB snapshots carry
 *  raw COLUMN_TYPE (`int(11)`, `bigint unsigned`, `tinyint(4) zerofill`) —
 *  the display width and unsigned/zerofill attributes are stripped from the
 *  integer family before classing (C3 sol finding 7; `tinyint(1)` still
 *  reads as booleanish via `isBooleanishDataType`, which sees the raw type). */
export function coarseTypeOfDataType(dataType: string | undefined): OperandLiteralSqlType {
  if (!dataType) return 'unknown';
  const t = dataType
    .trim()
    .toLowerCase()
    .replace(/\s+(unsigned|zerofill)\b/g, '')
    .replace(/^(tinyint|smallint|mediumint|int|integer|bigint)\s*\(\s*\d+\s*\)/, '$1');
  if (/^(tinyint|smallint|mediumint|int|integer|bigint|serial|bigserial|smallserial|int2|int4|int8|number\s*\(\s*\d+\s*\)|number)$/.test(t)) {
    return 'integer';
  }
  if (/^(decimal|numeric|real|float|double|double precision|money|smallmoney|binary_float|binary_double|number\s*\(.+\))\b/.test(t) || /^(decimal|numeric|float|real)\b/.test(t)) {
    return 'numeric';
  }
  if (/^(char|nchar|varchar|nvarchar|varchar2|nvarchar2|text|ntext|tinytext|mediumtext|longtext|clob|nclob|character varying|character|citext|enum|set)\b/.test(t)) {
    return 'text';
  }
  if (/^(bool|boolean)$/.test(t)) return 'boolean';
  return 'unknown';
}

/** D13 boolean-ish predicate: declared BOOL/BOOLEAN, MSSQL `BIT`,
 *  MySQL `TINYINT(1)`. Policy-local — the typeCheck mapper is NOT touched. */
export function isBooleanishDataType(dataType: string | undefined, engine: DatabaseEngineId): boolean {
  if (!dataType) return false;
  const t = dataType.trim().toLowerCase();
  if (t === 'bool' || t === 'boolean') return true;
  if (engine === 'mssql' && t === 'bit') return true;
  if (engine === 'mysql' && /^tinyint\s*\(\s*1\s*\)$/.test(t)) return true;
  return false;
}
